class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    BBCollectionHistory.init();
    const onHistoryChange = async (event) => {
      const searchParams = event.state?.searchParams ?? FacetFiltersForm.searchParamsInitial;
      if (!BBCollectionHistory.enabled && searchParams === FacetFiltersForm.searchParamsPrev) return;
      BBCollectionHistory.capture();
      const saved = BBCollectionHistory.select(event.state);
      const rendered = await FacetFiltersForm.renderPage(searchParams, null, false);
      if (rendered) BBCollectionHistory.restore(saved);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (updateURLHash) BBCollectionHistory.capture();
    const requestId = ++FacetFiltersForm.requestId;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    const requests = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      return FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId) :
        FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    return Promise.all(requests).then(() => requestId === FacetFiltersForm.requestId);
  }

  static renderSectionFromFetch(url, event, requestId) {
    return fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        if (requestId !== FacetFiltersForm.requestId) return;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (requestId !== FacetFiltersForm.requestId) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const searchPageTitle = parsedHTML.querySelector('[data-bb-search-page-title]')?.dataset.bbSearchPageTitle;
    if (searchPageTitle) {
      const titleDecoder = document.createElement('textarea');
      titleDecoder.innerHTML = searchPageTitle;
      document.title = titleDecoder.value;
    }
    const count = parsedHTML.getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const state = { searchParams };
    BBCollectionHistory.createEntry(state);
    history.pushState(state, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    form.querySelectorAll('price-range .number-slider input').forEach((input) => {
      if (input.value === '') formData.delete(input.name);
    });
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

class BBCollectionHistory {
  static init() {
    this.enabled = /\/collections\//.test(window.location.pathname);
    this.entries = new Map();
    if (!this.enabled) return;
    const state = { ...history.state, searchParams: window.location.search.slice(1) };
    this.createEntry(state);
    history.replaceState(state, '', window.location.href);
  }

  static createEntry(state) {
    if (!this.enabled) return;
    this.key = `bb-collection-${crypto.randomUUID()}`;
    state.bbCollectionKey = this.key;
  }

  static capture() {
    if (!this.enabled || !this.key) return;
    const container = document.getElementById('ProductGridContainer');
    if (!container || container.querySelector('.collection.loading, load-more[aria-busy="true"]')) return;
    this.entries.delete(this.key);
    this.entries.set(this.key, {
      html: container.innerHTML,
      layout: ['product-list', 'product-grid', 'product-gallery'].find(name => container.classList.contains(name)),
      scroll: window.scrollY,
    });
    while (this.entries.size > 8) this.entries.delete(this.entries.keys().next().value);
  }

  static select(state) {
    if (!this.enabled) return null;
    this.key = state?.bbCollectionKey;
    return this.entries.get(this.key);
  }

  static restore(saved) {
    if (!saved || !this.enabled) return;
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    container.innerHTML = saved.html;
    container.classList.remove('product-list', 'product-grid', 'product-gallery');
    if (saved.layout) container.classList.add(saved.layout);
    const selected = saved.layout === 'product-list' ? 'listv' : saved.layout === 'product-gallery' ? 'galleryv' : 'gridv';
    document.querySelectorAll('.wblistgridbtn').forEach(button => {
      button.classList.toggle('active', button.classList.contains(selected));
    });
    if (typeof syncLayoutButtons === 'function') syncLayoutButtons();
    const key = this.key;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (key === this.key) window.scrollTo({ top: saved.scroll, behavior: 'instant' });
    }));
  }
}

FacetFiltersForm.requestId = 0;
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('input', this.onRangeChange.bind(this));
    this.addEventListener('change', this.onRangeChange.bind(this));
  }

  connectedCallback() {
    const numberInputs = this.querySelectorAll('.number-slider input');
    if (numberInputs.length === 2) {
      this.syncValues(numberInputs[0]);
      this.syncValues(numberInputs[1]);
    }
  }

  onRangeChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('.number-slider input, .range-slider input')) return;
    this.syncValues(event.target);
  }

  syncValues(input) {
    const rangeInputs = this.querySelectorAll('.range-slider input');
    const numberInputs = this.querySelectorAll('.number-slider input');
    if (rangeInputs.length !== 2 || numberInputs.length !== 2) return;
    const isMinimum = input.classList.contains('rulerinput-min');
    const index = isMinimum ? 0 : 1;
    const floor = Number(rangeInputs[0].min) || 0;
    const ceiling = Number(rangeInputs[1].max);
    if (!Number.isFinite(ceiling)) return;
    const fallback = isMinimum ? floor : ceiling;
    const rawValue = input.value === '' ? fallback : Number(input.value);
    if (!Number.isFinite(rawValue)) return;
    let value = Math.min(ceiling, Math.max(floor, rawValue));
    const otherInput = numberInputs[isMinimum ? 1 : 0];
    const otherValue = otherInput.value === '' ? (isMinimum ? ceiling : floor) : Number(otherInput.value);
    if (Number.isFinite(otherValue)) {
      value = isMinimum ? Math.min(value, otherValue) : Math.max(value, otherValue);
    }
    value = Math.round(value * 100) / 100;
    rangeInputs[index].value = String(value);
    if (input.type === 'range' || input.value !== '') {
      numberInputs[index].value = String(value);
    }
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);